import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

/**
 * RH: ponto, escalas, treinamentos e avaliações.
 *
 * O card do site vende "registro de ponto, treinamentos, avaliações de
 * desempenho e portal do colaborador para consulta de escalas" — nada disso
 * existia; o módulo tinha só cadastro e cargos.
 */

interface Funcionario { id: number; nome: string; }
interface Certificado { id: number; titulo: string; valido_ate: string; funcionario: string; vencido: boolean; }
interface Escala { id: number; data: string; turno: string; funcionario: string; observacao?: string | null; }
interface Treinamento {
  id: number; id_funcionario: number; titulo: string; instituicao?: string | null;
  carga_horaria: number; status: string; valido_ate?: string | null;
}
interface Avaliacao {
  id: number; id_funcionario: number; periodo: string; data: string; nota_final: number;
  pontualidade: number; relacionamento: number; cuidado_pedagogico: number; iniciativa: number;
  pontos_fortes?: string | null; pontos_desenvolver?: string | null;
}

const hoje = () => {
  const d = new Date();
  return [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
};
const dataBR = (v?: string | null) => (v ? String(v).slice(0, 10).split('-').reverse().join('/') : '—');

type Aba = 'ponto' | 'escalas' | 'treinamentos' | 'avaliacoes';

const ABAS: { id: Aba; label: string }[] = [
  { id: 'ponto', label: '⏱️ Ponto' },
  { id: 'escalas', label: '📆 Escalas' },
  { id: 'treinamentos', label: '🎓 Treinamentos' },
  { id: 'avaliacoes', label: '⭐ Avaliações' },
];

const caixa: React.CSSProperties = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 16 };

export default function RhPainel() {
  const [aba, setAba] = useState<Aba>('ponto');
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [idFuncionario, setIdFuncionario] = useState(0);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');

  const [certificados, setCertificados] = useState<Certificado[]>([]);
  const [ponto, setPonto] = useState<any>(null);
  const [batidas, setBatidas] = useState({ entrada: '', saida_intervalo: '', volta_intervalo: '', saida: '' });
  const [dataPonto, setDataPonto] = useState(hoje());
  const [escalas, setEscalas] = useState<Escala[]>([]);
  const [treinamentos, setTreinamentos] = useState<Treinamento[]>([]);
  const [avaliacoes, setAvaliacoes] = useState<Avaliacao[]>([]);

  useEffect(() => {
    api.get('/funcionarios').then(({ data }) => {
      const lista = Array.isArray(data) ? data : (data?.items || data?.dados || []);
      setFuncionarios(lista);
      if (lista.length) setIdFuncionario(lista[0].id);
    }).catch(() => {});
    // Alerta de certificado vencendo: aparece independentemente da aba aberta.
    api.get('/rh/treinamentos/alertas').then(({ data }) => setCertificados(data?.certificados || [])).catch(() => {});
  }, []);

  const carregar = useCallback(async () => {
    if (!idFuncionario) return;
    setErro('');
    try {
      if (aba === 'ponto') {
        const { data } = await api.get(`/rh/ponto/${idFuncionario}`);
        setPonto(data);
      } else if (aba === 'escalas') {
        const { data } = await api.get('/rh/escalas');
        setEscalas(data?.escalas || []);
      } else if (aba === 'treinamentos') {
        const { data } = await api.get(`/rh/treinamentos?id_funcionario=${idFuncionario}`);
        setTreinamentos(Array.isArray(data) ? data : []);
      } else {
        const { data } = await api.get(`/rh/avaliacoes?id_funcionario=${idFuncionario}`);
        setAvaliacoes(Array.isArray(data) ? data : []);
      }
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Não foi possível carregar.');
    }
  }, [aba, idFuncionario]);

  useEffect(() => { carregar(); }, [carregar]);

  const avisar = (texto: string) => { setMsg(texto); setTimeout(() => setMsg(''), 4000); };

  const salvarPonto = async () => {
    setErro('');
    try {
      await api.post('/rh/ponto', { id_funcionario: idFuncionario, data: dataPonto, ...batidas });
      avisar('✅ Ponto registrado.');
      setBatidas({ entrada: '', saida_intervalo: '', volta_intervalo: '', saida: '' });
      carregar();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Falha ao registrar o ponto.');
    }
  };

  const nota = (v: number) => (v >= 4 ? '#2e7d32' : v >= 3 ? '#e65100' : '#c62828');

  return (
    <div style={{ marginBottom: 28 }}>
      {/* Certificados vencendo — risco de funcionamento, vem antes de tudo. */}
      {certificados.length > 0 && (
        <section style={{ ...caixa, background: '#fff4f4', border: '1px solid #f3c9c9', marginBottom: 16 }}>
          <h3 style={{ marginTop: 0, color: '#c62828' }}>⚠️ Certificados vencidos ou a vencer</h3>
          {certificados.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 14, padding: '4px 0' }}>
              <strong style={{ flex: '1 1 200px' }}>{c.funcionario}</strong>
              <span style={{ flex: '1 1 200px' }}>{c.titulo}</span>
              <span style={{ color: c.vencido ? '#c62828' : '#e65100', fontWeight: 700 }}>
                {c.vencido ? 'vencido em' : 'vence em'} {dataBR(c.valido_ate)}
              </span>
            </div>
          ))}
        </section>
      )}

      <section style={caixa}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {ABAS.map((a) => (
            <button key={a.id} onClick={() => setAba(a.id)}
              style={{ padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                border: aba === a.id ? '2px solid #1565c0' : '1px solid #ddd',
                background: aba === a.id ? '#e8f1fb' : '#fff' }}>
              {a.label}
            </button>
          ))}
          <label style={{ marginLeft: 'auto' }}>Funcionário
            <select value={idFuncionario} onChange={(e) => setIdFuncionario(Number(e.target.value))} style={{ display: 'block' }}>
              {funcionarios.length === 0 && <option value={0}>Nenhum cadastrado</option>}
              {funcionarios.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </label>
        </div>

        {erro && <div style={{ color: '#c62828', marginBottom: 8 }}>{erro}</div>}
        {msg && <div style={{ color: '#2e7d32', marginBottom: 8 }}>{msg}</div>}

        {aba === 'ponto' && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
              <label>Data<input type="date" value={dataPonto} onChange={(e) => setDataPonto(e.target.value)} style={{ display: 'block' }} /></label>
              <label>Entrada<input type="time" value={batidas.entrada} onChange={(e) => setBatidas({ ...batidas, entrada: e.target.value })} style={{ display: 'block' }} /></label>
              <label>Saída interv.<input type="time" value={batidas.saida_intervalo} onChange={(e) => setBatidas({ ...batidas, saida_intervalo: e.target.value })} style={{ display: 'block' }} /></label>
              <label>Volta interv.<input type="time" value={batidas.volta_intervalo} onChange={(e) => setBatidas({ ...batidas, volta_intervalo: e.target.value })} style={{ display: 'block' }} /></label>
              <label>Saída<input type="time" value={batidas.saida} onChange={(e) => setBatidas({ ...batidas, saida: e.target.value })} style={{ display: 'block' }} /></label>
              <button onClick={salvarPonto} disabled={!idFuncionario}
                style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#2e7d32', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                💾 Registrar
              </button>
            </div>

            {ponto?.resumo && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ flex: '1 1 180px', ...caixa, borderLeft: '4px solid #1565c0' }}>
                  <div style={{ color: '#888', fontSize: 13 }}>Total no mês</div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{ponto.resumo.totalFormatado}</div>
                </div>
                <div style={{ flex: '1 1 180px', ...caixa, borderLeft: '4px solid #2e7d32' }}>
                  <div style={{ color: '#888', fontSize: 13 }}>Dias fechados</div>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>{ponto.resumo.diasCompletos}</div>
                </div>
                <div style={{ flex: '1 1 180px', ...caixa, borderLeft: `4px solid ${ponto.resumo.diasIncompletos > 0 ? '#c62828' : '#eee'}` }}>
                  <div style={{ color: '#888', fontSize: 13 }}>Dias pendentes</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: ponto.resumo.diasIncompletos > 0 ? '#c62828' : '#333' }}>
                    {ponto.resumo.diasIncompletos}
                  </div>
                </div>
              </div>
            )}

            {(ponto?.dias || []).length === 0 ? (
              <div style={{ color: '#888' }}>Nenhum ponto registrado no mês.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                  <thead><tr style={{ background: '#fafafa', textAlign: 'left' }}>
                    {['Dia', 'Entrada', 'Intervalo', 'Saída', 'Trabalhado', 'Situação'].map((h) => (
                      <th key={h} style={{ padding: '8px 10px' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {ponto.dias.map((d: any) => (
                      <tr key={d.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 10px' }}>{dataBR(d.data)}</td>
                        <td style={{ padding: '8px 10px' }}>{d.entrada || '—'}</td>
                        <td style={{ padding: '8px 10px' }}>{d.saida_intervalo || '—'} / {d.volta_intervalo || '—'}</td>
                        <td style={{ padding: '8px 10px' }}>{d.saida || '—'}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 700 }}>
                          {d.jornada.incompleta ? '—' : `${Math.floor(d.jornada.minutosTrabalhados / 60)}h ${String(d.jornada.minutosTrabalhados % 60).padStart(2, '0')}min`}
                        </td>
                        <td style={{ padding: '8px 10px', color: d.jornada.incompleta ? '#c62828' : '#2e7d32', fontSize: 13 }}>
                          {d.jornada.alertas.length ? d.jornada.alertas.join(' ') : 'OK'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {aba === 'escalas' && (
          <EscalasAba funcionarios={funcionarios} escalas={escalas} aoMudar={carregar} aoErrar={setErro} aoAvisar={avisar} />
        )}

        {aba === 'treinamentos' && (
          <TreinamentosAba idFuncionario={idFuncionario} itens={treinamentos} aoMudar={carregar} aoErrar={setErro} aoAvisar={avisar} />
        )}

        {aba === 'avaliacoes' && (
          <AvaliacoesAba idFuncionario={idFuncionario} itens={avaliacoes} corNota={nota} aoMudar={carregar} aoErrar={setErro} aoAvisar={avisar} />
        )}
      </section>
    </div>
  );
}

function EscalasAba({ funcionarios, escalas, aoMudar, aoErrar, aoAvisar }: any) {
  const [form, setForm] = useState({ id_funcionario: 0, data: hoje(), turno: 'integral', observacao: '' });

  const criar = async () => {
    try {
      await api.post('/rh/escalas', { ...form, id_funcionario: form.id_funcionario || funcionarios[0]?.id });
      aoAvisar('✅ Escala criada.');
      aoMudar();
    } catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao criar a escala.'); }
  };
  const remover = async (id: number) => {
    try { await api.delete(`/rh/escalas/${id}`); aoMudar(); }
    catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao remover.'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
        <label>Funcionário
          <select value={form.id_funcionario} onChange={(e) => setForm({ ...form, id_funcionario: Number(e.target.value) })} style={{ display: 'block' }}>
            {funcionarios.map((f: Funcionario) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </label>
        <label>Data<input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} style={{ display: 'block' }} /></label>
        <label>Turno
          <select value={form.turno} onChange={(e) => setForm({ ...form, turno: e.target.value })} style={{ display: 'block' }}>
            <option value="matutino">Matutino</option><option value="vespertino">Vespertino</option>
            <option value="integral">Integral</option><option value="noturno">Noturno</option>
          </select>
        </label>
        <label style={{ flex: '1 1 200px' }}>Observação<input value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} style={{ display: 'block', width: '100%' }} /></label>
        <button onClick={criar} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1565c0', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Escalar</button>
      </div>
      {escalas.length === 0 ? <div style={{ color: '#888' }}>Sem escalas no mês.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {escalas.map((e: Escala) => (
            <div key={e.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 10px' }}>
              <strong style={{ minWidth: 92 }}>{dataBR(e.data)}</strong>
              <span style={{ flex: '1 1 160px' }}>{e.funcionario}</span>
              <span style={{ background: '#eef3f8', borderRadius: 6, padding: '2px 8px', fontSize: 13 }}>{e.turno}</span>
              {e.observacao && <span style={{ color: '#666', fontSize: 13 }}>{e.observacao}</span>}
              <button onClick={() => remover(e.id)} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>remover</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function TreinamentosAba({ idFuncionario, itens, aoMudar, aoErrar, aoAvisar }: any) {
  const [form, setForm] = useState({ titulo: '', instituicao: '', carga_horaria: 0, data_conclusao: '', valido_ate: '', status: 'concluido' });

  const criar = async () => {
    try {
      await api.post('/rh/treinamentos', { ...form, id_funcionario: idFuncionario });
      aoAvisar('✅ Treinamento registrado.');
      setForm({ titulo: '', instituicao: '', carga_horaria: 0, data_conclusao: '', valido_ate: '', status: 'concluido' });
      aoMudar();
    } catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao registrar.'); }
  };
  const remover = async (id: number) => {
    try { await api.delete(`/rh/treinamentos/${id}`); aoMudar(); }
    catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao remover.'); }
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
        <label style={{ flex: '1 1 220px' }}>Treinamento<input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Ex.: Primeiros socorros" style={{ display: 'block', width: '100%' }} /></label>
        <label>Instituição<input value={form.instituicao} onChange={(e) => setForm({ ...form, instituicao: e.target.value })} style={{ display: 'block' }} /></label>
        <label>Carga (h)<input type="number" value={form.carga_horaria} onChange={(e) => setForm({ ...form, carga_horaria: Number(e.target.value) })} style={{ display: 'block', width: 90 }} /></label>
        <label>Conclusão<input type="date" value={form.data_conclusao} onChange={(e) => setForm({ ...form, data_conclusao: e.target.value })} style={{ display: 'block' }} /></label>
        <label>Válido até<input type="date" value={form.valido_ate} onChange={(e) => setForm({ ...form, valido_ate: e.target.value })} style={{ display: 'block' }} /></label>
        <button onClick={criar} disabled={!form.titulo} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1565c0', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Registrar</button>
      </div>
      {itens.length === 0 ? <div style={{ color: '#888' }}>Nenhum treinamento registrado.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {itens.map((t: Treinamento) => (
            <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', border: '1px solid #f0f0f0', borderRadius: 8, padding: '8px 10px' }}>
              <strong style={{ flex: '1 1 200px' }}>{t.titulo}</strong>
              <span style={{ color: '#666', fontSize: 13 }}>{t.instituicao || '—'} · {t.carga_horaria}h</span>
              {t.valido_ate && <span style={{ fontSize: 13 }}>válido até {dataBR(t.valido_ate)}</span>}
              <span style={{ background: '#eef3f8', borderRadius: 6, padding: '2px 8px', fontSize: 13 }}>{t.status}</span>
              <button onClick={() => remover(t.id)} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>remover</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function AvaliacoesAba({ idFuncionario, itens, corNota, aoMudar, aoErrar, aoAvisar }: any) {
  const vazio = { periodo: '', pontualidade: 0, relacionamento: 0, cuidado_pedagogico: 0, iniciativa: 0, pontos_fortes: '', pontos_desenvolver: '' };
  const [form, setForm] = useState(vazio);
  const CRITERIOS: { campo: keyof typeof vazio; label: string }[] = [
    { campo: 'pontualidade', label: 'Pontualidade' },
    { campo: 'relacionamento', label: 'Relacionamento' },
    { campo: 'cuidado_pedagogico', label: 'Cuidado pedagógico' },
    { campo: 'iniciativa', label: 'Iniciativa' },
  ];

  const criar = async () => {
    try {
      await api.post('/rh/avaliacoes', { ...form, id_funcionario: idFuncionario });
      aoAvisar('✅ Avaliação registrada.');
      setForm(vazio);
      aoMudar();
    } catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao registrar a avaliação.'); }
  };
  const remover = async (id: number) => {
    try { await api.delete(`/rh/avaliacoes/${id}`); aoMudar(); }
    catch (e: any) { aoErrar(e?.response?.data?.message || 'Falha ao remover.'); }
  };

  return (
    <>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', marginBottom: 10 }}>
          <label>Período<input value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })} placeholder="Ex.: 1º semestre/2026" style={{ display: 'block' }} /></label>
          {CRITERIOS.map((c) => (
            <label key={c.campo}>{c.label}
              <select value={form[c.campo] as number} onChange={(e) => setForm({ ...form, [c.campo]: Number(e.target.value) })} style={{ display: 'block' }}>
                <option value={0}>—</option>
                {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
          <label style={{ flex: '1 1 240px' }}>Pontos fortes<input value={form.pontos_fortes} onChange={(e) => setForm({ ...form, pontos_fortes: e.target.value })} style={{ display: 'block', width: '100%' }} /></label>
          <label style={{ flex: '1 1 240px' }}>A desenvolver<input value={form.pontos_desenvolver} onChange={(e) => setForm({ ...form, pontos_desenvolver: e.target.value })} style={{ display: 'block', width: '100%' }} /></label>
          <button onClick={criar} disabled={!form.periodo} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#1565c0', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>+ Avaliar</button>
        </div>
        <div style={{ color: '#888', fontSize: 12.5, marginTop: 6 }}>
          Notas de 1 a 5. Critério deixado em "—" fica <strong>fora</strong> da média — não conta como zero.
        </div>
      </div>

      {itens.length === 0 ? <div style={{ color: '#888' }}>Nenhuma avaliação registrada.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {itens.map((a: Avaliacao) => (
            <div key={a.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ flex: '1 1 160px' }}>{a.periodo}</strong>
                <span style={{ color: '#888', fontSize: 13 }}>{dataBR(a.data)}</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: corNota(Number(a.nota_final)) }}>{Number(a.nota_final).toFixed(2)}</span>
                <button onClick={() => remover(a.id)} style={{ border: 'none', background: 'transparent', color: '#c62828', cursor: 'pointer' }}>remover</button>
              </div>
              <div style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
                Pontualidade {a.pontualidade || '—'} · Relacionamento {a.relacionamento || '—'} · Cuidado {a.cuidado_pedagogico || '—'} · Iniciativa {a.iniciativa || '—'}
              </div>
              {a.pontos_fortes && <div style={{ fontSize: 13, marginTop: 4 }}>✅ {a.pontos_fortes}</div>}
              {a.pontos_desenvolver && <div style={{ fontSize: 13, color: '#8a6d00' }}>🎯 {a.pontos_desenvolver}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
